/**
 * Vendor Detail Page Component
 *
 * Displays complete vendor information including:
 * - Basic vendor information and contact details
 * - Address and location data
 * - Criticality rating visualization
 * - Associated contracts table
 * - Edit and delete actions
 */

import React from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Mail,
  Phone,
  Globe,
  MapPin,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { useVendor, useDeleteVendor } from '../services/vendorService';
import { format } from 'date-fns';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Rating } from '@/components/ui/rating';
import { ContractStatusBadge, ActiveStatusBadge } from '@/components/ui/status-badge';
import { useAuth } from '@/contexts/AuthContext';
import { formatCalendarDate } from '../utils/formatters';

/**
 * Format currency value in USD
 */
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Detail item component - horizontal layout
 */
function DetailItem({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between py-3 border-b border-border">
      <span className="text-sm text-grey-500">{label}</span>
      <span className="text-sm font-medium text-right max-w-[60%]">{value || '\u2014'}</span>
    </div>
  );
}

/**
 * Vendor Detail Page Component
 */
const VendorDetail: React.FC = () => {
  const navigate = useNavigate();
  const { canManageVendors } = useAuth();
  const params = useParams({ strict: false }) as { id: string };
  const vendorId = params.id;

  const { data: vendor, isLoading, error, isError } = useVendor(vendorId);
  const deleteVendor = useDeleteVendor();

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

  /**
   * Handle delete vendor
   */
  const handleDelete = async () => {
    if (!vendor) return;

    try {
      await deleteVendor.mutateAsync(vendorId);
      setDeleteDialogOpen(false);
      navigate({ to: '/vendors' });
    } catch (error) {
      console.error('Failed to delete vendor:', error);
      alert('Failed to delete vendor. It may have associated contracts.');
    }
  };

  /**
   * Loading state
   */
  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  /**
   * Error state
   */
  if (isError || !vendor) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load vendor details: {error?.message || 'Vendor not found'}
          </AlertDescription>
        </Alert>
        <Button variant="ghost" onClick={() => navigate({ to: '/vendors' })} className="mt-4">
          <ArrowLeft className="h-4 w-4" />
          Back to Vendors
        </Button>
      </div>
    );
  }

  // Build address string
  const addressParts = [
    vendor.address,
    [vendor.city, vendor.state, vendor.postalCode].filter(Boolean).join(', '),
    vendor.country,
  ].filter(Boolean);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Page Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate({ to: '/vendors' })} className="mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back to Vendors
        </Button>

        <div className="flex justify-between items-start flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">{vendor.name}</h1>
            <ActiveStatusBadge isActive={vendor.isActive} />
          </div>

          {canManageVendors && (
            <div className="flex gap-2">
              <Button onClick={() => navigate({ to: `/vendors/${vendorId}/edit` })}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Main Vendor Information - Combined Card */}
        <div className="col-span-12 lg:col-span-8">
          <Card className="h-full">
            <CardContent className="p-6">
              <div className="grid grid-cols-12 gap-8">
                {/* Left Column - Vendor Info */}
                <div className="col-span-12 md:col-span-6">
                  <p className="text-xs font-medium text-grey-500 uppercase tracking-wider mb-4">
                    Vendor Information
                  </p>

                  {vendor.description && (
                    <div className="mb-6">
                      <p className="text-sm text-grey-500 mb-1">Description</p>
                      <p className="text-sm">{vendor.description}</p>
                    </div>
                  )}

                  <DetailItem
                    label="Criticality Rating"
                    value={<Rating value={vendor.criticalityRating} size="sm" />}
                  />

                  {vendor.website && (
                    <div className="flex justify-between py-3 border-b border-border">
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-grey-400" />
                        <span className="text-sm text-grey-500">Website</span>
                      </div>
                      <a
                        href={vendor.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                      >
                        {vendor.website.replace(/^https?:\/\//, '')}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  )}

                  {addressParts.length > 0 && (
                    <>
                      <p className="text-xs font-medium text-grey-500 uppercase tracking-wider mt-6 mb-4">
                        Address
                      </p>
                      <div className="flex gap-3">
                        <MapPin className="h-4 w-4 text-grey-400 mt-0.5 shrink-0" />
                        <div>
                          {addressParts.map((part, index) => (
                            <p key={index} className="text-sm">
                              {part}
                            </p>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Right Column - Contact Info & Notes */}
                <div className="col-span-12 md:col-span-6">
                  <p className="text-xs font-medium text-grey-500 uppercase tracking-wider mb-4">
                    Contact Information
                  </p>

                  <DetailItem label="Primary Contact" value={vendor.primaryContactName} />

                  <div className="flex justify-between py-3 border-b border-border">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-grey-400" />
                      <span className="text-sm text-grey-500">Email</span>
                    </div>
                    <a
                      href={`mailto:${vendor.primaryContactEmail}`}
                      className="text-sm text-primary font-medium hover:underline"
                    >
                      {vendor.primaryContactEmail}
                    </a>
                  </div>

                  {vendor.primaryContactPhone && (
                    <div className="flex justify-between py-3 border-b border-border">
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-grey-400" />
                        <span className="text-sm text-grey-500">Phone</span>
                      </div>
                      <span className="text-sm font-medium">{vendor.primaryContactPhone}</span>
                    </div>
                  )}

                  {vendor.notes && (
                    <>
                      <p className="text-xs font-medium text-grey-500 uppercase tracking-wider mt-6 mb-4">
                        Notes
                      </p>
                      <p className="text-sm whitespace-pre-wrap">{vendor.notes}</p>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Metadata */}
        <div className="col-span-12 lg:col-span-4">
          <Card className="h-full bg-grey-50">
            <CardContent className="p-6">
              <p className="text-xs font-medium text-grey-500 uppercase tracking-wider mb-4">
                Record Information
              </p>
              <DetailItem label="Status" value={vendor.isActive ? 'Active' : 'Inactive'} />
              <DetailItem label="Total Contracts" value={vendor.contracts.length} />
              <DetailItem
                label="Total Contract Value"
                value={formatCurrency(vendor.contracts.reduce((sum, c) => sum + c.totalContractValue, 0))}
              />
              <DetailItem
                label="Created"
                value={format(new Date(vendor.createdAt), 'MMM dd, yyyy')}
              />
              <DetailItem
                label="Last Updated"
                value={format(new Date(vendor.updatedAt), 'MMM dd, yyyy')}
              />
            </CardContent>
          </Card>
        </div>

        {/* Associated Contracts */}
        <div className="col-span-12">
          <Card>
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">
                  Associated Contracts ({vendor.contracts.length})
                </h2>
              </div>
              <Separator className="mb-4" />

              {vendor.contracts.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-grey-500">No contracts associated with this vendor</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Contract Name</TableHead>
                      <TableHead>Start Date</TableHead>
                      <TableHead>End Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Value</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vendor.contracts.map((contract) => (
                      <TableRow key={contract.id} className="hover:bg-grey-50">
                        <TableCell>
                          <span className="text-sm font-medium">{contract.title}</span>
                        </TableCell>
                        <TableCell>
                          {formatCalendarDate(contract.startDate)}
                        </TableCell>
                        <TableCell>
                          {formatCalendarDate(contract.endDate)}
                        </TableCell>
                        <TableCell>
                          <ContractStatusBadge status={contract.status} />
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-sm font-medium">
                            {formatCurrency(contract.totalContractValue)}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigate({ to: `/contracts/${contract.id}` })}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>View Contract</TooltipContent>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Vendor Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vendor</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete vendor "{vendor.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-white hover:bg-destructive-dark"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VendorDetail;
